/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import RequireForAccess from 'react-access';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Paper from '@material-ui/core/Paper';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import ActionsList from 'components/ActionsList/ActionsList';
import Activity from 'components/Activity/Activity';
import Availability from 'components/Availability/Availability';
import Loading from 'components/Loading/Loading';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { updateRegion, viewRegion } from 'permissions/account';

import type { TypeAvailability } from 'components/Availability/Availability';

import CaaSDetails from './CaaSDetails';
import TradingAccountDetails from './TradingAccountDetails/TradingAccountDetails';
import TradingAccountRegion from './TradingAccountRegion/TradingAccountRegion';
import AssetRequestDetails from './AssetRequestDetails/AssetRequestDetails';
import AssetCustomFields from './AssetCustomFields/AssetCustomFields';
import AssignedToTradingAccount from './AssignedToTradingAccount/AssignedToTradingAccount';
import DocumentsAndResources from './DocumentsAndResources/DocumentsAndResources';

import actionsListData from './actionsListData';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from 'common-scss/DetailsPage.module.scss';
import { generateUrl, account } from 'api/routes';
import ActionDrawerWrapper from '../../../../components/ActionDrawerWrapper/ActionDrawerWrapper';
import {
  ArchiveAccount,
  AssignAccount,
  DisableAccount,
  EnableAccount,
  ExportAssetData,
  RemoveUserAccess,
  UnarchiveAccount,
} from '../../../ActionDrawer';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

@translate()
@inject('accountStore', 'actionDrawerStore')
@observer
export default class AccountDetailsTab extends React.Component<*> {
  render() {
    const { accountStore, actionDrawerStore } = this.props;
    const { details } = accountStore;
    const t: Function = this.props.t;

    const tradingAccountPreferences: Array<TypeAvailability> = [
      {
        id: 1,
        label: t('general:assets.labels.allow_sale_consignment'),
        content: details.details.sale_consignment,
      },
      {
        id: 2,
        label: t('general:assets.labels.allow_sub_rental_program'),
        content: details.details.subrental_program,
      },
      {
        id: 3,
        label: t('general:assets.labels.allow_new_asset_request'),
        content: details.details.asset_request_form_enabled,
      },
      {
        id: 5,
        label: t('general:assets.labels.allow_new_license_request'),
        content: details.details.license_request_form_enabled,
      },
    ];

    const americasOnly = details.details.region.name === 'AMERICAS';
    const allowRushAsset: TypeAvailability = {
      id: 4,
      label: t('general:assets.labels.allow_new_rush_asset_request'),
      content: details.details.is_rush_asset_request_form_customer,
    };

    if (americasOnly) {
      tradingAccountPreferences.push(allowRushAsset);
      tradingAccountPreferences.sort((a, b) => a.id - b.id);
    }

    return (
      <div className={styles['grid-wrapper']}>
        {accountStore.loadingDetails ? (
          <Loading />
        ) : (
          <>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <TradingAccountDetails details={details} t={t} />

                <RequireForAccess permissions={[...updateRegion, ...viewRegion]}>
                  <TradingAccountRegion details={details} t={t} />
                </RequireForAccess>

                <Availability
                  title={t('general:trading_accounts.labels.preferences')}
                  details={tradingAccountPreferences}
                />

                <CaaSDetails details={details.details} t={t} />

                <AssetRequestDetails
                  assetRequestEnabled={details.details.asset_request_form_enabled}
                  rushAssetRequestEnabled={details.details.is_rush_asset_request_form_customer}
                  assetRequestEmailAddress={details.details.asset_request_email_address}
                  rushAssetRequestEmailAddress={details.details.rush_asset_request_email_address}
                  tradingAccountPrefix={details.details.asset_request_prefix}
                  t={t}
                />

                <AssetCustomFields accountStore={accountStore} details={details.details} />

                <AssignedToTradingAccount accountStore={accountStore} details={details} t={t} />

                <DocumentsAndResources accountStore={accountStore} t={t} />
              </div>

              <div className={styles['actions-panel']}>
                <StatusIndicator
                  title={t('general:trading_accounts.labels.status')}
                  status={
                    details.details.archived_at
                      ? 'Archived'
                      : details.details.is_enabled
                      ? 'Active'
                      : 'Disabled'
                  }
                />
                <ActionsList data={details.details} actions={actionsListData} />
              </div>
            </Paper>

            <Paper className={styles['activity-panel']}>
              {accountStore.accountId && (
                <Activity
                  title={true}
                  url={generateUrl(account.activity, [accountStore.accountId])}
                  refresh={this.props.actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </>
        )}
        <ActionDrawerWrapper
          appendActionToURL={true}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              archive: <ArchiveAccount currentAccountDetails={accountStore.details.details} />,
              unarchive: <UnarchiveAccount currentAccountDetails={accountStore.details.details} />,
              assignUser: <AssignAccount currentAccountDetails={accountStore.details.details} />,
              disable: <DisableAccount currentAccountDetails={accountStore.details.details} />,
              export: <ExportAssetData currentAccountDetails={accountStore.details.details} />,
              enable: <EnableAccount currentAccountDetails={accountStore.details.details} />,
              removeUser: <RemoveUserAccess currentAccountDetails={accountStore.details.details} />,
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </div>
    );
  }
}
